import moment from 'moment';
import { State } from '@meraki-internal/state';
import { IOperator } from '../profile/IOperator';
import { OperatorState } from '../profile/OperatorState';
import { TrackingService } from '../support/tracking/TrackingService';
import { Clock } from '../support/Clock';

export interface IAffiliateLink {
    offer: string;
    affiliate: string;
    isApplicable?: (operator: IOperator) => boolean;
    heroImageUrl: string;
    showInMenu?: boolean;

    // empty only when we're doing experiments (trying to learn which to add)
    link: string;

    // required for interstitial page
    instructionsSvgPath?: string;
    continueButton?: string;
}

export class AffiliateLinkState extends State<Record<string, never>> {
    static inject = () => [
        TrackingService,
        OperatorState,
        Clock
    ];
    constructor(
        private tracking: TrackingService,
        private operatorState: OperatorState,
        private clock: Clock
    ) {
        super({});

        // make clock state fire at significant transitions
        // (do this via the clock so it works when mocking)
        this.clock.registerEmitAt('2025-05-22T05:00:00.000Z', '2025-05-30T05:00:00.000Z');
        this.clock.subscribe(() => this.setState({}));
    }

    showingLink?: IAffiliateLink;

    getAffiliateLinks = (): IAffiliateLink[] => {
        return this.affiliateLinks.filter(a => !a.isApplicable || a.isApplicable(this.operatorState.state));
    };

    cancel = () => {
        if (this.showingLink){
            const link = this.showingLink;
            this.tracking.trackWithDebounce('Affiliate Link Cancelled', () => ({
                affiliate: link.affiliate,
                offer: link.offer,
                continue: link.continueButton
            }));
            this.showingLink = undefined;
            this.setState({ });
        }
    };

    private affiliateLinks: IAffiliateLink[] = [
        {
            // training for recreational pilots (regular discount outside special time range)
            affiliate: 'uav-coach',
            offer: '$100 off Part 107 Training!',
            isApplicable: operator => operator.certificationType !== 'part-107' && !moment(this.clock.now()).isBetween('2025-05-22T05:00:00.000Z', '2025-05-30T05:00:00.000Z'),
            link: 'https://www.dronepilotgroundschool.com/autopylot-partner/?affcode=23044_8goydlyu&coupon_code=pylot100',
            showInMenu: true,
            heroImageUrl: 'https://149355317.v2.pressablecdn.com/wp-content/uploads/2024/03/Copy-of-DPGS-Powered-by-logo-9-e1721761124214.png',
        },
        {
            // training for recreational pilots (extra discount inside special time range)
            affiliate: 'uav-coach',
            offer: '$140 off Part 107 Test Prep!',
            isApplicable: operator => operator.certificationType !== 'part-107' && moment(this.clock.now()).isBetween('2025-05-22T05:00:00.000Z', '2025-05-30T05:00:00.000Z'),
            link: 'https://www.dronepilotgroundschool.com/?affcode=23044_8goydlyu&coupon=MEMORIAL25',
            showInMenu: true,
            heroImageUrl: 'https://149355317.v2.pressablecdn.com/wp-content/uploads/2024/03/Copy-of-DPGS-Powered-by-logo-9-e1721761124214.png'
        },
        {
            // training for part 107 pilots
            affiliate: 'uav-coach',
            offer: '20% off Drone Training!',
            isApplicable: operator => operator.certificationType === 'part-107',
            link: 'https://uavcoach.com/autopylot-partner/?affcode=2023044_8goydlyu&coupon_code=AUTOPYLOT',
            showInMenu: true,
            heroImageUrl: 'https://uploads.teachablecdn.com/attachments/UCrtZYQSmmjHuFS9dkJa_DME.png'
        },
    ];

    // this is likely not worth it once more perks is a static...
    // but while we're in learning mode, this helps hide the More Pilot Perks button
    anyNotInMenu = () => {
        return this.getAffiliateLinks().some(a => !a.showInMenu);
    };

    affiliateLinkClicked = async (link: IAffiliateLink, { source }: { source: 'menu' | 'marketplace' }) => {
        this.tracking.trackWithDebounce('Affiliate Link Clicked', () => ({
            source,
            affiliate: link.affiliate,
            offer: link.offer
        }));

        if (link.continueButton) {
            // show interstitial modal
            this.showingLink = link;
            this.setState({ });
        } else {
            // no interstitual modal, go directly to affiliate site
            this.goToAffiliate(link);
        }
    };

    affiliateLinkContinued = async (link: IAffiliateLink) => {
        this.tracking.trackWithDebounce('Affiliate Link Continued', () => ({
            affiliate: link.affiliate,
            offer: link.offer,
            continue: link.continueButton
        }));

        this.goToAffiliate(link);

        this.showingLink = undefined;
        this.setState({ });
    };

    private goToAffiliate = (link: IAffiliateLink) => {
        window.open(link.link, '_blank');
    };
}
